import Uppy, { Meta, UppyFile } from '@uppy/core';
import '@uppy/core/dist/style.css';
import React, { FC, ReactNode, useEffect, useState, useRef } from 'react';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';
import { useOrganizationContext } from '@/contexts/organization-context';
import { getMediaPath, getUppy } from '@/lib/uppy.util';
import MediaElement from '../media-element';
import { Progress } from '@/components/shadcn/progress';
import { Media } from '@/__generated__/graphql';
import Upload from '@/assets/icons/upload-cloud.svg';
import Image from 'next/image';
import { Button } from '@/components/shadcn/button';
import { UploadIcon } from '@radix-ui/react-icons';
import { cn } from '@/lib/utils';
import Text from '@/components/basic/text';

type PreviewType = { fileId: string; previewUrl?: string };

export enum AssetsType {
  assets = 'assets',
  posts = 'posts',
  logos = 'logos',
}

const UppyDropzone: FC<{
  onUploadingStarted: () => void;
  onUploadFileReady?: (fileId: string, url: string) => void;
  onUploadError?: (fileName: string) => void;
  onCancelUploading?: () => void;
  isDisabled?: boolean;
  assetsType?: AssetsType;
  dropzoneText?: string;
  children?: ReactNode;
  className?: string;
}> = ({
  onUploadingStarted,
  onUploadFileReady,
  onUploadError,
  isDisabled,
  assetsType,
  dropzoneText = 'Drop files here or click to upload',
  children,
  className = '',
}) => {
  const session = useSessionContext();
  const [progressState, setProgress] = useState<number>(0);
  const [previews, setPreviews] = useState<PreviewType[]>([]);
  const [uppy, setUppy] = useState<Uppy>();
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const { organizationId } = useOrganizationContext();
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (
      !session.loading &&
      session.doesSessionExist &&
      session.accessTokenPayload['st-role']?.v
    ) {
      setUppy(getUppy());
    }
  }, [session]);

  const onUploadSuccess = (
    file: UppyFile<Meta, Record<string, Media>> | undefined,
    response: any
  ) => {
    const mediaId = getMediaPath(response.uploadURL);
    if (!mediaId) {
      onUploadError &&
        onUploadError(
          `file name:'${file?.name || ''}' upload url:'${response.uploadURL || ''}'`
        );
      setPreviews((previews) =>
        previews.filter((preview) => preview.fileId !== file?.id)
      );
      return;
    }

    onUploadFileReady && onUploadFileReady(mediaId, response.uploadURL || '');
    setPreviews((previews) =>
      previews.filter((preview) => preview.fileId !== file?.id)
    );
  };

  const onUploadErrorEvent = (
    file: UppyFile<Meta, Record<string, Media>> | undefined,
    error: Error,
    response?: any
  ) => {
    onUploadError && onUploadError(file?.name || 'file');
    setPreviews((previews) =>
      previews.filter((preview) => preview.fileId !== file?.id)
    );
  };

  const onProgress = (progress: number) => {
    onUploadingStarted && onUploadingStarted();
    setProgress(progress);
  };

  const onThumbnail = (
    file: UppyFile<Meta, Record<string, Media>>,
    previewUrl: string
  ) => {
    onUploadingStarted && onUploadingStarted();
    if (!file?.response?.uploadURL) {
      setPreviews((previews) =>
        previews
          .filter((item) => item.fileId !== file.id)
          .concat({ fileId: file.id, previewUrl })
      );
    }
  };

  const onUploadProgress = (
    file: UppyFile<Meta, Record<string, Media>> | undefined,
    progress: any
  ) => {
    onUploadingStarted && onUploadingStarted();
    if (file?.id) {
      setPreviews((previews) =>
        previews.some((item) => item.fileId === file?.id)
          ? previews
          : previews.concat({ fileId: file?.id })
      );
    }
  };

  useEffect(() => {
    if (uppy) {
      const metaOptions = {
        ...(organizationId && {
          org_id: organizationId,
        }),
        ...(assetsType && { assets_type: assetsType }),
      };

      uppy.setOptions({ meta: metaOptions });
      uppy.on('upload-success', onUploadSuccess);
      uppy.on('upload-error', onUploadErrorEvent);
      uppy.on('progress', onProgress);
      uppy.on('upload-progress', onUploadProgress);
      uppy.on('thumbnail:generated', onThumbnail);

      return () => {
        uppy.off('upload-success', onUploadSuccess);
        uppy.off('upload-error', onUploadErrorEvent);
        uppy.off('progress', onProgress);
        uppy.off('upload-progress', onUploadProgress);
        uppy.off('thumbnail:generated', onThumbnail);
      };
    }
  }, [uppy]);

  useEffect(() => {
    if (!previews?.length) {
      setProgress(0);
    }
  }, [previews]);

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDisabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDisabled) {
      setIsDragging(true);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (isDisabled || !uppy) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      Array.from(files).forEach((file) => {
        try {
          uppy.addFile({
            name: file.name,
            type: file.type,
            data: file,
            source: 'user',
            isRemote: false,
          });
        } catch (err) {
          uppy.log(err);
        }
      });
    }
  };

  const handleClick = () => {
    if (isDisabled || !uppy) return;
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isDisabled || !uppy || !e.target.files?.length) return;

    Array.from(e.target.files).forEach((file) => {
      try {
        uppy.addFile({
          name: file.name,
          type: file.type,
          data: file,
          source: 'user',
          isRemote: false,
        });
      } catch (err) {
        uppy.log(err);
      }
    });

 
    e.target.value = '';
  };

  if (!uppy) {
    return null;
  }

  return (
    <div className={className}>
      <div
        className={cn(
          'cursor-pointer rounded-lg border-2 border-dashed p-6 mt-4 transition-colors duration-200',
          isDragging
            ? 'border-blue-500 bg-blue-50'
            : 'bg-primary-50 p-3 py-5',
          isDisabled ? 'cursor-not-allowed opacity-50' : ''
        )}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type='file'
          className='hidden'
          onChange={handleFileInputChange}
          disabled={isDisabled}
          accept='image/*,video/*'
          multiple
        />

        {children || (
          <div className='flex flex-col items-center justify-center text-center'>
            <Image src={Upload} alt='upload' />
            <Text  className='mt-2 text-sm text-primary-600'>{dropzoneText}</Text>
            <Button className='mt-4 bg-primary-50 border-primary-600' variant={'secondary'}>
              <UploadIcon /> Upload
            </Button>
          </div>
        )}
      </div>

      {!!previews?.length && (
        <>
          <p className='mt-2'>Uploading...</p>
          <Progress value={progressState} className='mb-2 mt-2' />
          <div className='rounded border border-gray-200'>
            <div className='m-2 flex flex-wrap gap-2'>
              {previews.map((preview) => (
                <MediaElement key={preview.fileId} url={preview.previewUrl} />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UppyDropzone;
